import React, { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { SiteEvent } from '../../../redux/api/eventsApi';
import { SEVERITY_HEX_COLORS } from '../../../constants/eventStyles';

interface SafetyIssuesModuleProps {
  events: SiteEvent[];
  showSiteName?: boolean;
  onViewSafety?: () => void;
  onEventClick?: (eventId: string) => void;
}

function formatTimeAgo(dateString: string): string {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const diffMs = now - then;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w`;
}

const STATUS_LABELS: Record<string, string> = {
  open: 'Active',
  acknowledged: 'Under review',
  in_progress: 'In progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const SafetyIssuesModule: React.FC<SafetyIssuesModuleProps> = ({ events, showSiteName, onViewSafety, onEventClick }) => {
  const safetyEvents = useMemo(() => {
    return events.filter(e => {
      const isSafetyType = e.event_type === 'incident';
      const isHighSeverity = e.severity === 'critical' || e.severity === 'high';
      const isOpenCritical = e.status === 'open' && e.severity === 'critical';
      return (isSafetyType && isHighSeverity) || isOpenCritical;
    }).sort((a, b) => {
      const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      return severityOrder[a.severity] - severityOrder[b.severity];
    });
  }, [events]);

  const countLabel = showSiteName
    ? `${safetyEvents.length} active safety flags across portfolio`
    : `${safetyEvents.length} active safety flags`;

  if (safetyEvents.length === 0) {
    return (
      <div className="flex items-center gap-2 py-3 px-1">
        <AlertTriangle className="w-4 h-4 text-[#17c480]" />
        <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          No active safety flags
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4" style={{ color: SEVERITY_HEX_COLORS.high }} />
        <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
          {countLabel}
        </span>
      </div>

      <div className="space-y-2">
        {safetyEvents.map(event => (
          <div
            key={event.id}
            className="flex items-start gap-2 py-2 px-2 rounded-lg transition-colors hover:bg-[var(--color-surface-hover)] cursor-pointer"
            onClick={() => onEventClick?.(event.id)}
          >
            <span
              className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
              style={{ backgroundColor: SEVERITY_HEX_COLORS[event.severity] }}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                {event.title}
              </p>
              {showSiteName && (
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  Site: {event.site_id}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                {STATUS_LABELS[event.status] || event.status}
              </span>
              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                {formatTimeAgo(event.occurred_at)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SafetyIssuesModule;
